/**
 * `PUT /api/cart` builder — quantity change.
 *
 * Pure delivery-layer helper: assembles the request body, posts it, throws on
 * non-2xx. AbortController and timeout policy stay at the caller (cart
 * provider) — same split as `fetch-cart-lines.ts`. PUT returns
 * `{ success: true }` which we don't parse (write-path is fire-and-ack); a
 * shape-drift detection schema lives in `@kraahl/contract` for future use.
 *
 * @see packages/contract/src/cart-update.ts — request contract
 * @see app/api/cart/route.ts PUT — handler
 */

import type { CartUpdateQuantityRequest } from "@kraahl/contract";

export interface UpdateCartQuantityArgs {
  id: string;
  qty: number;
  userId?: string | null;
  guestId?: string | null;
  signal?: AbortSignal;
}

export async function updateCartQuantity(args: UpdateCartQuantityArgs): Promise<void> {
  const body: CartUpdateQuantityRequest = {
    id: args.id,
    qty: args.qty,
    ...(args.userId != null ? { userId: args.userId } : {}),
    ...(args.guestId != null ? { guestId: args.guestId } : {}),
  };

  const res = await fetch("/api/cart", {
    method: "PUT",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(body),
    ...(args.signal ? { signal: args.signal } : {}),
  });

  if (!res.ok) {
    throw new Error(`HTTP ${res.status}`);
  }
}
